
import { Vue, Component, toNative } from 'vue-facing-decorator';
import SideBarPane from './SideBarPane.vue';
import EmailPreviewPane from './EmailPreviewPane.vue';
import ConfigureEmailEditPane from './ConfigureEmailEditPane.vue';
import { EnvironmentBaseURL, FetchData, Root, Store } from '@/main';
import EmailCategoriesModel from '@/entities/Invite/EmailCategoriesModel';
import EmailTemplates from '../Templates/EmailTemplates.vue';
import { EmailTemplate } from '@/entities/Invite/EmailTemplateViewModel';
import EmailTemplatesModel from '@/entities/Invite/EmailTemplatesModel';
import ConfigureEmailsHelper from '@/helper/ConfigureEmailsHelper';
import { CorporateFontEntry } from '@/entities/Invite/CorporateFontModel';
import Toaster from '@/components/Common/Toaster.vue';
import VueModalPopUp from '@/components/Common/VueModalPopUp.vue';
import ApiContentType from '@/enums/ApiContentType';
import Loading from '@/components/Common/loading.vue';
import EmailTemplatesTableModel from '@/entities/Invite/EmailTemplatesTableModel';
import InviteConfirmationEmailEnum from '@/enums/InviteConfirmationEmailEnum';
import { StoreHelper } from '@/store/StoreHelper';
import { AppColorSettingsInfo } from '@/entities/Application/AppColorSettingsInfo';
import { AppRequestInfo } from '@/entities/AppRequestInfo';
import InviteActionTypeEnum from '@/enums/InviteActionTypeEnum';
import InviteEmailTypeEnum from '@/enums/InviteEmailTypeEnum';
import InviteEventWebPageEmailTypeEnum from '@/enums/InviteEventWebPageEmailTypeEnum';

@Component({
  components: {
    SideBarPane,
    EmailPreviewPane,
    ConfigureEmailEditPane,
    EmailTemplates,
    Toaster,
    VueModalPopUp,
    Loading,
  },
})
class ConfigureEmail extends Vue {
  // Static assets and navigation paths
  private backIcon = require('@/assets/Images/back-arrow-icon.svg');
  // Hero image Blob
  private heroImageBlob = ConfigureEmailsHelper.HeroImageUrl;
  // Calender Fav Icon
  private calenderIcon: string = require('@/assets/Images/Invite/calender-icon.png');

  // UI state management flags
  private showPreview = false;
  private isEditModeOn = false;
  private showChangeTemplateScreen = false;
  private isBackButtonVisible = false;
  private isMobile = false;
  private showEmailListingInMobile = true;
  private showToaster = false;
  private unsavedChangesInEditPane = false;
  // private showUnsavedModal = false;
  private showLoading = false;
  private loaderBorderColor = '';

  // Variable storing email information
  private emailCategories: EmailCategoriesModel[] = [];

  // Corporate fonts available for this application, fetched from the API and passed to the edit pane
  private corporateFonts: CorporateFontEntry[] = [];

  // Variable holding selected email details and subsequently passed to children
  private emailTemplate = EmailTemplate.createEmpty();
  private selectedTemplateForEmail = EmailTemplate.createEmpty();
  private templatesCatalogueGallery: EmailTemplate[] = [];
  private selectedEmailTab = '';
  private toasterMessage = '';
  private toasterType = ConfigureEmailsHelper.ToasterInfoType;
  private toasterDefaultTimeout = ConfigureEmailsHelper.ToasterTimeOut;
  private loaderDefaultTimeout = ConfigureEmailsHelper.LoaderTimeOut;


  // Page Title
  private pageHeader = '';
  // Back Button label variable for mobile view
  private backButtonLabel = '';

  // Default Header,Footer colors and logo
  private defaultBackgroundColor = '';
  private defaultForegroundColor = '';
  private defaultLogo = '';

  // Mounted life cycle hook
  private mounted(){
    if(window.innerWidth <= 767){
      this.isMobile = true;
    }else{
      this.isMobile = false;
    }
    this.backButtonLabel = this.$t('Button.Back').toString();
    this.GetListOfTemplates();
    this.GetCorporateFonts();
    this.GetDefaultDataFromStoreHelper();
    this.GetSendGridHTML();
    this.pageHeader = this.$t('Invite.ConfigureEmails').toString();
    Root.on('template-selected', (templateData: EmailCategoriesModel) => {
      this.emailTemplate.AvailableTemplates = [];
      this.ProcessSelectedTemplate(templateData);
      if(this.isMobile && this.showEmailListingInMobile){
        this.pageHeader = '';
        this.backButtonLabel = this.$t('Invite.ConfigureEmails.MobileBackButtonTitle').toString();
      } else{
        this.backButtonLabel = this.$t('Button.Back').toString();
      }
      this.showPreview = true;
      this.showEmailListingInMobile = false;
      this.selectedEmailTab = templateData.EmailType;
    });
  }

  // Get Default Header & Footer colors and logo url
  private GetDefaultDataFromStoreHelper(){
    const storeHelper = new StoreHelper(Store);
    const reqInfo: AppRequestInfo = storeHelper.GetRequestInfoFromStore();
    const appcolors: AppColorSettingsInfo =  storeHelper.GetApplicationColorSettingsFromStore();
    this.defaultBackgroundColor = appcolors.topNavBackgroundColor;
    this.defaultForegroundColor = appcolors.topNavForegroundColor;
    this.defaultLogo = reqInfo.LogoURI;
  }

  // TO BE USED POST SAVE TO FETCH FRESH DATA FROM DB
  private ReloadTemplateData() {
    this.templatesCatalogueGallery = [];
    this.emailTemplate.AvailableTemplates = [];
    // Find the selected templates by EmailType
    const selectedTemplates = this.emailCategories.find((template: EmailCategoriesModel) => template.EmailType === this.selectedEmailTab);
    // If templates are found, continue processing
    if (selectedTemplates !== null && selectedTemplates !== undefined) {
      // Remove the template for which isSelected is true
      this.ProcessSelectedTemplate(selectedTemplates);
    }
  }

  // Generates individuals SendGrid Template from all the templates data for a selected email type and stores it in templatesCatalogueGallery
  private GenerateAllSendGridTemplates(templates: EmailTemplatesModel[]){
    this.templatesCatalogueGallery = [];
    const mySelected = templates.find((template) => template.IsSelected === true);
    if(!!mySelected){
      this.emailTemplate.SelectedTemplate = mySelected;

    }
    const defaultTemplates = templates.filter((template) => template.IsDefault === true);
    defaultTemplates[1].HeroImageUrl = this.heroImageBlob;
    const otherTemplates = templates.filter((template) => template.IsDefault === false);
    // Sort the first two templates as the ones with IsDefault = true
    const sortedTemplates = [...defaultTemplates, ...otherTemplates];
    sortedTemplates.forEach((template: EmailTemplatesModel) => {
      this.emailTemplate.TemplateData = template;
      this.GenerateSendGridHTML(template);
      this.templatesCatalogueGallery.push({ ...this.emailTemplate });
    });
  }

  // Function for processing selected template
  private ProcessSelectedTemplate(templateData: EmailCategoriesModel){

    for(const template of templateData.Templates){
      if(!template.IsSelected && !template.IsDefault){
        this.emailTemplate.AvailableTemplates.push({
          Id: template.Id,
          TemplateName: template.TemplateName,
          ModifiedOn: template.ModifiedOn,
        });
      }
    }
    this.GenerateAllSendGridTemplates(templateData.Templates);
    // Find the selected template for the current email type
    const selectedTemplate = this.templatesCatalogueGallery.find((template: EmailTemplate) => template.TemplateData.IsSelected === true);
    // Remove the templated in which isSelected is true
    this.templatesCatalogueGallery = this.templatesCatalogueGallery.filter((template: EmailTemplate) => template.TemplateData.IsSelected === false || (template.TemplateData.IsDefault === true && template.TemplateData.IsSelected === true));
    if(selectedTemplate !== null && selectedTemplate !== undefined){
      this.emailTemplate = selectedTemplate;
      this.emailTemplate.SelectedTemplate = selectedTemplate.TemplateData;
      this.selectedTemplateForEmail = selectedTemplate;
    }
  }

  // Common function for showing and hiding toaster message and loader
  private ShowToasterAndLoader(toasterMessage: string) {
    this.toasterMessage = toasterMessage;
    this.showToaster = true;
    setTimeout(() => {
      this.showLoading = false;
      setTimeout(() => {
        this.showToaster = false;
      }, this.toasterDefaultTimeout);
    }, this.loaderDefaultTimeout);
  }

  // Fetches the corporate fonts for this application, then loads templates so the gallery
  // HTML is generated with the @font-face rules available for any corporate font in use
  private GetCorporateFonts(){
    FetchData('/Registration/GetCorporateFonts', '').then((response) => {
      this.corporateFonts = !!response && !!response.CorporateFonts ? response.CorporateFonts : [];
      ConfigureEmailsHelper.RegisterCorporateFonts(this.corporateFonts);
    }).catch(() => {
      // log errors if any
    });
  }

  // Fetches email categories and templates from the server
  private GetListOfTemplates(){
    FetchData('/Registration/GetListOfEmailTemplates', '').then((response) => {
      this.emailCategories = response;
      this.ReloadTemplateData();
    }).catch(() => {
      // console error if any
    });
  }

  // Get Selected template on discard changes after applying a new template
  private GetSelectedTemplate(){
    this.emailTemplate = this.selectedTemplateForEmail;
  }

  // Fetches the raw HTML template from SendGrid
  private GetSendGridHTML(){
    FetchData('/Registration/GetCustomTemplate', '').then((response) => {
      this.emailTemplate.RawHtml = response;
    }).catch(()=>{
      // logg errors if any
    });
  }

  // Emit Receiver on Apply Template screen change
  private SetEmailConfigurationEditPaneData(template: EmailTemplate){
    this.emailTemplate = template;
    this.showChangeTemplateScreen = false;
    this.isEditModeOn = true;
    this.showToaster = true;
    this.toasterType = ConfigureEmailsHelper.ToasterInfoType;
    this.toasterMessage = this.$t('Invite.ConfigureEmails.BlueToasterMessage').toString();
  }

  // Hanlder for Back button visibilty
  private BackButtonHandler(val: boolean){
    this.isBackButtonVisible = val;
  }

  // Function for managing back navigation in mobile
  private HandleMobileBackNavigation(){
    if(this.showEmailListingInMobile){
      window.location.href = this.GetRedirectionUrl();
    }
    this.showEmailListingInMobile = true;
    this.backButtonLabel = this.$t('Button.Back').toString();
  }

  // function listing to the redirection to Email templates screen
  private NavigateToChangeTemplateScreen(){
    this.showChangeTemplateScreen = true;
    this.isEditModeOn = false;
  }

  // Function that sets email configurations
  private  SaveEmailConfigurations(formData: any , moveToHomeScreen: boolean , moveToTemplatesScreen: boolean){
    this.showLoading = true;
    FetchData('/Registration/SaveEmailConfiguration', formData, ApiContentType.FormData).then(() => {
      this.toasterType = ConfigureEmailsHelper.ToasterType;
      this.ShowToasterAndLoader(this.$t('Invite.ConfigureEmails.SavedEmailSettings').toString());
      this.GetListOfTemplates();
      if(moveToHomeScreen){
        this.isEditModeOn = false;
      } else if(moveToTemplatesScreen){
        this.isEditModeOn = false;
        this.showChangeTemplateScreen = true;
      }
      this.unsavedChangesInEditPane = false;
    }).catch(()=>{
      this.showLoading = false;
    });
  }

  // Function use to remove template from templates catalouge
  private RemoveTemplate(id: number){
    this.emailCategories = this.emailCategories.map((category: EmailCategoriesModel) => {
      if (category.EmailType === this.selectedEmailTab) {
        return {
          ...category,
          Templates: category.Templates.filter((template) => template.Id !== id),
        };
      }
      return category;
    });
    this.templatesCatalogueGallery = this.templatesCatalogueGallery.filter((template: EmailTemplate) => template.TemplateData.Id !== id);
    this.emailTemplate.AvailableTemplates = this.emailTemplate.AvailableTemplates.filter((template: EmailTemplatesTableModel) => template.Id !== id);
  }

  // Function use to rename a selected template
  private RenameTemplate(id: number , templateNewName: string){
    const lastUpdatedDate = new Date().toISOString();
    this.emailCategories = this.emailCategories.map((category: EmailCategoriesModel) => {
      if (category.EmailType !== this.selectedEmailTab) {
        return category;
      }
      return {
        ...category,
        Templates: category.Templates.map((template: EmailTemplatesModel) =>
          template.Id === id
            ? {
              ...template,
              TemplateName: templateNewName,
              ModifiedOn: lastUpdatedDate,
            }
            : template,
        ),
      };
    });
    this.templatesCatalogueGallery = this.templatesCatalogueGallery.map(
      (template: EmailTemplate) => {
        if (template.TemplateData.Id === id) {
          return {
            ...template,
            TemplateData: {
              ...template.TemplateData,
              TemplateName: templateNewName,
              ModifiedOn: lastUpdatedDate,
            },
          };
        }

        return template;
      },
    );
    this.emailTemplate.AvailableTemplates = this.emailTemplate.AvailableTemplates.map((template: EmailTemplatesTableModel) => {
      if(template.Id === id){
        template.TemplateName = templateNewName;
        template.ModifiedOn = lastUpdatedDate;
      }
      return template;
    });
  }


  // Function for checking unsaved changes check from edit page
  private DirtyCheckInEditPage(val:  boolean ){
    this.unsavedChangesInEditPane = val;
  }

  // Processes the Send Grid HTML from selected template
  private GenerateSendGridHTML(templateData: EmailTemplatesModel) {
    let bodyHtml = templateData.EmailBody;
    bodyHtml = templateData.EmailBody.replace(/<img\s+[^>]*src=(['"])\{\{AddToCalendarIMG\}\}\1[^>]*\/?>/gi,
      `<img src="${this.calenderIcon}" alt="Calendar" />`);
    templateData.EmailBody = templateData.EmailBody.replace(/<img\s+[^>]*src=(['"])\{\{AddToCalendarIMG\}\}\1[^>]*\/?>/gi,
      '{{AddToCalendarIMG}}');
    const backgroundColor   = templateData.BackgroundColor || this.defaultBackgroundColor;
    const buttonText  = templateData.LabelText || ConfigureEmailsHelper.RegisterOrEditButtonText;
    const textColor   = templateData.LabelColor || this.defaultForegroundColor;

    if(templateData.HasRegistrationLink){
      bodyHtml = this.ReplaceButtonTag(bodyHtml, backgroundColor, buttonText, textColor, 'RegistrationLinkOrButton', InviteActionTypeEnum.Link);
    } else{
      bodyHtml = this.ReplaceButtonTag(bodyHtml, backgroundColor, buttonText, textColor, 'RegistrationLinkOrButton', InviteActionTypeEnum.Button);
    }

    if(templateData.TemplateType === InviteEventWebPageEmailTypeEnum.Invitation || templateData.TemplateType === InviteEventWebPageEmailTypeEnum.InvitationReminder || templateData.TemplateType === InviteEventWebPageEmailTypeEnum.Custom){
      const eventWebPageBg    = templateData.EventWebPageAccessBackgroundColor || this.defaultBackgroundColor;
      const eventWebPageText  = templateData.EventWebPageAccessLabelText || ConfigureEmailsHelper.RegisterOrEditButtonText;
      const eventWebPageColor = templateData.EventWebPageAccessLabelColor || this.defaultForegroundColor;
      if(templateData.HasEventWebPageAccessLink){
        bodyHtml = this.ReplaceButtonTag(bodyHtml, eventWebPageBg, eventWebPageText, eventWebPageColor, 'EventWebPageLink', InviteActionTypeEnum.Link);
      } else {
        bodyHtml = this.ReplaceButtonTag(bodyHtml, eventWebPageBg, eventWebPageText, eventWebPageColor, 'EventWebPageLink', InviteActionTypeEnum.Button);
      }
    }

    if(templateData.TemplateType === InviteConfirmationEmailEnum.Confirmation || templateData.TemplateType === InviteConfirmationEmailEnum.ConfirmationTickets){
      const editBackgroundColor = templateData.EditResponsesBackgroundColor || this.defaultBackgroundColor;
      const editButtonText = templateData.EditResponsesLabelText || ConfigureEmailsHelper.RegisterOrEditButtonText;
      const editTextColor = templateData.EditResponsesLabelColor || this.defaultForegroundColor;
      if(templateData.HasEditResponsesRegistrationLink){
        bodyHtml = this.ReplaceButtonTag(bodyHtml, editBackgroundColor, editButtonText, editTextColor, 'EditResponsesLink',InviteActionTypeEnum.Link);
      }else{
        bodyHtml = this.ReplaceButtonTag(bodyHtml, editBackgroundColor, editButtonText, editTextColor, 'EditResponsesLink',InviteActionTypeEnum.Button);
      }
      const eventAccessBackgroundColor   = templateData.EventAccessBackgroundColor || this.defaultBackgroundColor;
      const eventAccessLabelText  = templateData.EventAccessLabelText || ConfigureEmailsHelper.EventAccessButtonText;
      const eventAccessLabelColor   = templateData.EventAccessLabelColor || this.defaultForegroundColor;
      const storeHelper = new StoreHelper(Store);
      const reqInfo: AppRequestInfo = storeHelper.GetRequestInfoFromStore();
      if(templateData.HasEventAccessLink){
        bodyHtml = this.ReplaceButtonTag(bodyHtml, eventAccessBackgroundColor, eventAccessLabelText, eventAccessLabelColor, 'EventAccessLink', InviteActionTypeEnum.Link);
        bodyHtml = reqInfo.EnablePersonalisedEventAccessLink ? this.ReplaceButtonTag(bodyHtml, eventAccessBackgroundColor, eventAccessLabelText, eventAccessLabelColor, 'PersonalEventAccessLink', InviteActionTypeEnum.Link) : bodyHtml;
      } else {
        bodyHtml = this.ReplaceButtonTag(bodyHtml, eventAccessBackgroundColor, eventAccessLabelText, eventAccessLabelColor, 'EventAccessLink', InviteActionTypeEnum.Button);
        bodyHtml = reqInfo.EnablePersonalisedEventAccessLink ? this.ReplaceButtonTag(bodyHtml, eventAccessBackgroundColor, eventAccessLabelText, eventAccessLabelColor, 'PersonalEventAccessLink', InviteActionTypeEnum.Button) : bodyHtml;
      }
    }

    const html = this.ReplacePlaceholders(this.emailTemplate.RawHtml, templateData, bodyHtml);
    this.emailTemplate.ProcessedHtml = html;
  }

  // Replaces the tags inside the raw SendGrid HTML with selected template data
  private ReplacePlaceholders(raw: string, template: EmailTemplatesModel, emailBody: string): string {
    return raw
      .replace(/{{LOGO_URI}}/g, template.LogoUrl || (!!template.HeaderAccentColor ? '' : this.defaultLogo))
      .replace(/{{HERO_IMAGE_URL}}/g, template.HeroImageUrl)
      .replace(/{{LOGO_POSITION}}/g, template.LogoPosition || ConfigureEmailsHelper.LogoPosition)
      .replace(/{{{CORPORATE_FONTS}}}/g, '')
      .replace(/{{HEADER_ACCENT_COLOR}}/g, template.HeaderAccentColor || this.defaultBackgroundColor)
      .replace(/{{EMAIL_ACCENT_COLOR}}/g, template.EmailAccentColor || ConfigureEmailsHelper.EmailAccentColor)
      .replace(/{{EMAIL_BODY_TEXT_COLOR}}/g, template.EmailTextColor || ConfigureEmailsHelper.EmailTextColor)
      .replace(/{{FOOTER_ACCENT_COLOR}}/g, template.FooterAccentColor || this.defaultBackgroundColor)
      .replace(/{{FOOTER_TEXT_COLOR}}/g, template.FooterTextColor || this.defaultForegroundColor)
      .replace(/{{{BODY_CONTENT}}}/g, emailBody)
      .replace(/{{{FOOTER_CONTENT}}}/g, template.FooterContent || ConfigureEmailsHelper.FooterContent);
  }


  // Created button from tags to be used in Send Grid Template
  private CreateCTAButton(backgroundColor: string, label: string , labelColor: string) {
    return `<a style='background-color: ${backgroundColor}; color: ${labelColor}; text-decoration: none; font-weight: bold; padding: 12px 24px; display: inline-block; border-radius: 10px;'>${label}</a>`;
  }

  // Create Link from tag
  private CreateCTALink(label: string , labelColor: string){
    return `<a style=' color: ${labelColor}; text-decoration: underline;'>${label}</a>`;
  }

  // Handler for creating buttons on SendGrid
  private ReplaceButtonTag(body: string, backgroundColor: string, label: string,labelColor: string, placeholder: string , type: number) {
    let cta = '';
    if(type === InviteActionTypeEnum.Button){
      cta = this.CreateCTAButton(backgroundColor, label , labelColor);
    } else{
      cta = this.CreateCTALink(label, labelColor);
    }
    return body.replace(new RegExp(`{{${placeholder}}}`, 'gi'), cta);
  }


  // Handler for navigating back to the MVC
  private GetRedirectionUrl() {
    return EnvironmentBaseURL + ConfigureEmailsHelper.InvitePath;
  }

  // Back navigation for Custom Email Templates screen
  private HandleBackNavigation(){
    if (this.isEditModeOn && this.unsavedChangesInEditPane){
      if(this.$refs.emailEditPane !== null && this.$refs.emailEditPane !== undefined) {
        (this.$refs.emailEditPane as any).OpenUnsavedChangesinEditPane();
      }
    } else if (this.isEditModeOn) {
      this.pageHeader = this.$t('Invite.ConfigureEmails').toString();
      this.isEditModeOn = false;
      this.showToaster = false;
    } else if (this.showChangeTemplateScreen) {
      this.showChangeTemplateScreen = false;
      this.isEditModeOn = true;
      this.showToaster = false;
    } else {
      window.location.href = this.GetRedirectionUrl();
    }
  }

  // Handler to change page title in Configure Email Page header
  private ChangePageTitle(){
    this.isEditModeOn = true;
    if(this.selectedEmailTab === InviteEmailTypeEnum.InvitationTickets || this.selectedEmailTab === InviteEmailTypeEnum.ReminderTickets || this.selectedEmailTab === InviteEmailTypeEnum.ConfirmationTickets || this.selectedEmailTab === InviteEmailTypeEnum.ReminderCompleteRegistration){
      this.pageHeader = this.emailTemplate.TemplateData.EmailTypeProp + this.$t('Invite.ConfigureEmails.Tickets').toString();
    }else{
      this.pageHeader = this.emailTemplate.TemplateData.EmailTypeProp;
    }
  }
}
export default toNative(ConfigureEmail);
